import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  FiCheck,
  FiEye,
  FiEyeOff,
  FiLock,
  FiMail,
  FiUser,
  FiAlertCircle,
  FiCalendar,
} from "react-icons/fi";
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  CadastroAlunoFieldErrors,
  getPasswordStrength,
  validateCadastroAlunoForm,
} from "../../helpers/cadastroAlunoValidation";
import {
  getRegisterErrorMessage,
  registerUser,
} from "../../services/register";
import linksthree from "../../services/linksthree";
import { LinkThreeData } from "../../types";
import logoWhite from "../../assets/LogoCabecalhoBranca.png";
import utils from "../../helpers/utils";

function CadastroAlunoPage() {
  const { id } = useParams();
  const nameRef = useRef<HTMLInputElement>(null);

  const [professional, setProfessional] = useState<LinkThreeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<CadastroAlunoFieldErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [photoBroken, setPhotoBroken] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [birthDate, setBirthDate] = useState("");

  const passwordStrength = useMemo(
    () => getPasswordStrength(password),
    [password],
  );

  const photoSrc = useMemo(() => {
    if (!professional?.photo || photoBroken) return "";
    return utils.getUriImage(
      professional.photo,
      professional.id != null ? String(professional.id) : "",
    );
  }, [professional?.photo, professional?.id, photoBroken]);

  useEffect(() => {
    setPhotoBroken(false);
  }, [professional?.photo, professional?.id]);

  useEffect(() => {
    async function load() {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const result = await linksthree.getButtons(id);
        if (!result?.id && !result?.name) {
          setProfessional(null);
        } else {
          setProfessional(result);
        }
      } catch {
        setProfessional(null);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  useEffect(() => {
    if (!loading && professional?.id && !success) {
      const t = setTimeout(() => nameRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [loading, professional?.id, success]);

  const clearFieldError = (field: keyof CadastroAlunoFieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    if (formError) setFormError("");
  };

  const validate = (): boolean => {
    const next = validateCadastroAlunoForm({
      name,
      email,
      password,
      birth_date: birthDate,
    });
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError("");
    if (!validate()) return;
    if (!professional?.id) {
      setFormError("Profissional não encontrado.");
      return;
    }

    setSubmitting(true);
    try {
      await registerUser({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        birth_date: birthDate.trim(),
        phone: "",
        professional_id: professional.id,
      });
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setFormError(getRegisterErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const accent = professional?.color || "#a855f7";
  const accentSoft = professional?.color_secondary || "#ec4899";
  const bgBase = professional?.background || "#070B14";
  const firstName = (professional?.name || "").split(/\s+/)[0] || "seu personal";

  const inputClass = (hasError?: string) =>
    [
      "w-full rounded-2xl border bg-white/[0.04] py-3.5 pl-11 pr-4 text-[15px] text-white outline-none transition placeholder:text-white/35",
      "focus:bg-white/[0.07] focus:ring-2 focus:ring-offset-0",
      hasError
        ? "border-rose-400/60 focus:border-rose-400 focus:ring-rose-400/25"
        : "border-white/10 focus:border-white/30 focus:ring-white/15",
    ].join(" ");

  if (loading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center px-4"
        style={{ backgroundColor: "#070B14" }}
        data-testid="cadastro-aluno-loading"
      >
        <div className="w-full max-w-md animate-pulse space-y-6">
          <div className="mx-auto h-7 w-28 rounded bg-white/10" />
          <div className="mx-auto h-24 w-24 rounded-full bg-white/10" />
          <div className="mx-auto h-6 w-48 rounded bg-white/10" />
          <div className="space-y-3 pt-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-14 w-full rounded-2xl bg-white/10" />
            ))}
            <div className="h-14 w-full rounded-2xl bg-white/15" />
          </div>
        </div>
      </div>
    );
  }

  if (!professional?.id) {
    return (
      <div
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#070B14] px-6 text-center"
        data-testid="cadastro-aluno-not-found"
      >
        <img src={logoWhite} alt="EasyFit" className="h-8 opacity-80" />
        <div className="max-w-sm rounded-3xl border border-white/10 bg-white/[0.04] px-6 py-8">
          <FiAlertCircle className="mx-auto mb-3 h-10 w-10 text-white/50" />
          <h1 className="text-xl font-semibold text-white">
            Link indisponível
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-white/55">
            Este link de cadastro não existe ou não está mais ativo. Peça um
            novo link ao seu personal.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen overflow-hidden"
      style={{ backgroundColor: bgBase }}
      data-testid="cadastro-aluno-page"
    >
      {/* Atmosphere */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute -left-24 -top-24 h-80 w-80 rounded-full opacity-40 blur-3xl"
          style={{ background: accent }}
        />
        <div
          className="absolute -bottom-32 -right-16 h-96 w-96 rounded-full opacity-30 blur-3xl"
          style={{ background: accentSoft }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,transparent_0%,rgba(0,0,0,0.55)_70%)]" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col px-4 py-8 sm:py-12">
        <div className="mb-8 flex justify-center">
          <img src={logoWhite} alt="EasyFit" className="h-7 opacity-90 sm:h-8" />
        </div>

        <div className="flex flex-1 flex-col justify-center">
          {/* Personal hero */}
          <div className="mb-7 flex flex-col items-center text-center">
            <div className="relative mb-4">
              {photoSrc ? (
                <img
                  src={photoSrc}
                  alt={professional.name || "Personal"}
                  className="h-24 w-24 rounded-full object-cover shadow-2xl ring-4 ring-white/20 sm:h-28 sm:w-28"
                  onError={() => setPhotoBroken(true)}
                  data-testid="cadastro-aluno-photo"
                />
              ) : (
                <div
                  className="flex h-24 w-24 items-center justify-center rounded-full text-3xl font-bold text-white shadow-2xl ring-4 ring-white/20 sm:h-28 sm:w-28"
                  style={{
                    background: `linear-gradient(135deg, ${accent}, ${accentSoft})`,
                  }}
                  data-testid="cadastro-aluno-photo-fallback"
                >
                  {(professional.name || "P").charAt(0).toUpperCase()}
                </div>
              )}
              <span className="absolute bottom-1 right-1 h-4 w-4 rounded-full border-2 border-[#070B14] bg-emerald-400" />
            </div>

            <p className="mb-1 text-xs font-medium uppercase tracking-[0.18em] text-white/45">
              Convite de treino
            </p>
            <h1
              className="text-2xl font-bold tracking-tight text-white sm:text-3xl"
              data-testid="cadastro-aluno-title"
            >
              Treine com {firstName}
            </h1>
            <p className="mt-2 max-w-xs text-sm leading-relaxed text-white/60">
              Crie sua conta em menos de 1 minuto e já fique vinculado ao seu
              personal no EasyFit.
            </p>
          </div>

          <div className="rounded-[28px] border border-white/10 bg-white/[0.06] p-5 shadow-2xl backdrop-blur-xl sm:p-7">
            {success ? (
              <div
                className="space-y-6 text-center"
                data-testid="cadastro-aluno-success"
              >
                <div
                  className="mx-auto flex h-16 w-16 items-center justify-center rounded-full text-white shadow-lg"
                  style={{
                    background: `linear-gradient(135deg, ${accent}, ${accentSoft})`,
                  }}
                >
                  <FiCheck size={32} strokeWidth={2.5} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white">
                    Conta criada!
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">
                    Você já está vinculado a{" "}
                    <span className="font-medium text-white">
                      {professional.name}
                    </span>
                    . Baixe o app e entre com o mesmo e-mail e senha.
                  </p>
                </div>

                <ol className="space-y-2 text-left">
                  {[
                    "Baixe o EasyFit na loja do seu celular",
                    "Abra o app e entre com seu e-mail e senha",
                    "Pronto — seu personal já te verá na lista",
                  ].map((step, i) => (
                    <li
                      key={step}
                      className="flex items-start gap-3 rounded-2xl bg-white/[0.04] px-3.5 py-3"
                    >
                      <span
                        className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                        style={{ backgroundColor: accent }}
                      >
                        {i + 1}
                      </span>
                      <span className="text-sm text-white/80">{step}</span>
                    </li>
                  ))}
                </ol>

                <div className="flex flex-col gap-3 sm:flex-row" data-testid="store-links">
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="store-app-store"
                    className="inline-flex flex-1 items-center justify-center gap-3 rounded-2xl bg-black px-4 py-3.5 text-white transition hover:scale-[1.02] hover:bg-zinc-900"
                  >
                    <svg
                      className="h-7 w-7 flex-shrink-0"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" />
                    </svg>
                    <div className="text-left">
                      <div className="text-[10px] uppercase tracking-wide opacity-70">
                        Baixar na
                      </div>
                      <div className="text-sm font-semibold leading-tight">
                        App Store
                      </div>
                    </div>
                  </a>
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="store-play-store"
                    className="inline-flex flex-1 items-center justify-center gap-3 rounded-2xl bg-black px-4 py-3.5 text-white transition hover:scale-[1.02] hover:bg-zinc-900"
                  >
                    <svg
                      className="h-7 w-7 flex-shrink-0"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                    </svg>
                    <div className="text-left">
                      <div className="text-[10px] uppercase tracking-wide opacity-70">
                        Disponível no
                      </div>
                      <div className="text-sm font-semibold leading-tight">
                        Google Play
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="space-y-4"
                noValidate
                data-testid="cadastro-aluno-form"
              >
                <div>
                  <label
                    htmlFor="cadastro-name"
                    className="mb-1.5 block text-sm font-medium text-white/75"
                  >
                    Nome completo
                  </label>
                  <div className="relative">
                    <FiUser className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-white/35" />
                    <input
                      ref={nameRef}
                      id="cadastro-name"
                      type="text"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        clearFieldError("name");
                      }}
                      className={inputClass(fieldErrors.name)}
                      placeholder="Nome e sobrenome"
                      autoComplete="name"
                      aria-invalid={!!fieldErrors.name}
                    />
                  </div>
                  {fieldErrors.name ? (
                    <p className="mt-1.5 text-xs text-rose-300">
                      {fieldErrors.name}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label
                    htmlFor="cadastro-email"
                    className="mb-1.5 block text-sm font-medium text-white/75"
                  >
                    E-mail
                  </label>
                  <div className="relative">
                    <FiMail className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-white/35" />
                    <input
                      id="cadastro-email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        clearFieldError("email");
                      }}
                      className={inputClass(fieldErrors.email)}
                      placeholder="seu@email.com"
                      autoComplete="email"
                      inputMode="email"
                      aria-invalid={!!fieldErrors.email}
                    />
                  </div>
                  {fieldErrors.email ? (
                    <p className="mt-1.5 text-xs text-rose-300">
                      {fieldErrors.email}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label
                    htmlFor="cadastro-password"
                    className="mb-1.5 block text-sm font-medium text-white/75"
                  >
                    Senha
                  </label>
                  <div className="relative">
                    <FiLock className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-white/35" />
                    <input
                      id="cadastro-password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        clearFieldError("password");
                      }}
                      className={`${inputClass(fieldErrors.password)} pr-12`}
                      placeholder="Mín. 8 caracteres"
                      autoComplete="new-password"
                      aria-invalid={!!fieldErrors.password}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-white/45 transition hover:bg-white/10 hover:text-white"
                      aria-label={
                        showPassword ? "Ocultar senha" : "Mostrar senha"
                      }
                    >
                      {showPassword ? (
                        <FiEyeOff size={18} />
                      ) : (
                        <FiEye size={18} />
                      )}
                    </button>
                  </div>
                  {password ? (
                    <div className="mt-2">
                      <div className="mb-1 flex gap-1">
                        {[1, 2, 3].map((level) => (
                          <div
                            key={level}
                            className="h-1 flex-1 rounded-full transition-colors"
                            style={{
                              backgroundColor:
                                passwordStrength.score >= level
                                  ? passwordStrength.score === 1
                                    ? "#f43f5e"
                                    : passwordStrength.score === 2
                                      ? "#f59e0b"
                                      : "#34d399"
                                  : "rgba(255,255,255,0.12)",
                            }}
                          />
                        ))}
                      </div>
                      <p className="text-xs text-white/45">
                        Força:{" "}
                        <span className="text-white/70">
                          {passwordStrength.label}
                        </span>
                      </p>
                    </div>
                  ) : null}
                  {fieldErrors.password ? (
                    <p className="mt-1.5 text-xs text-rose-300">
                      {fieldErrors.password}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label
                    htmlFor="cadastro-birth-date"
                    className="mb-1.5 block text-sm font-medium text-white/75"
                  >
                    Data de nascimento
                  </label>
                  <div className="relative">
                    <FiCalendar className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
                    <input
                      id="cadastro-birth-date"
                      type="date"
                      value={birthDate}
                      onChange={(e) => {
                        setBirthDate(e.target.value);
                        clearFieldError("birth_date");
                      }}
                      className={[
                        "w-full rounded-2xl border bg-white/[0.04] py-3.5 pl-11 pr-4 text-[15px] text-white outline-none transition placeholder:text-white/35",
                        "focus:bg-white/[0.07] focus:ring-2 [color-scheme:dark]",
                        fieldErrors.birth_date
                          ? "border-rose-400/60 focus:border-rose-400 focus:ring-rose-400/25"
                          : "border-white/10 focus:border-white/30 focus:ring-white/15",
                      ].join(" ")}
                      max={new Date().toISOString().slice(0, 10)}
                      aria-invalid={!!fieldErrors.birth_date}
                      data-testid="cadastro-aluno-birth-date"
                    />
                  </div>
                  {fieldErrors.birth_date ? (
                    <p className="mt-1.5 text-xs text-rose-300">
                      {fieldErrors.birth_date}
                    </p>
                  ) : null}
                </div>

                {formError ? (
                  <div
                    role="alert"
                    className="flex items-start gap-2 rounded-2xl border border-rose-400/30 bg-rose-500/10 px-3.5 py-3 text-sm text-rose-200"
                  >
                    <FiAlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>{formError}</span>
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="cadastro-aluno-submit"
                  className="group relative mt-2 flex w-full items-center justify-center gap-2 overflow-hidden rounded-2xl px-4 py-4 text-[15px] font-semibold text-white shadow-lg transition hover:scale-[1.01] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100"
                  style={{
                    background: `linear-gradient(135deg, ${accent}, ${accentSoft})`,
                    boxShadow: `0 12px 40px -12px ${accent}`,
                  }}
                >
                  {submitting ? (
                    <>
                      <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      Criando sua conta...
                    </>
                  ) : (
                    "Criar conta e continuar"
                  )}
                </button>

                <p className="pt-1 text-center text-[11px] leading-relaxed text-white/40">
                  Ao continuar, você concorda em usar o EasyFit com o
                  acompanhamento de {firstName}.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CadastroAlunoPage;
